import { useRef, useEffect } from "react";
import { motion } from "motion/react";
import { ArrowUpRight, Calendar } from "lucide-react";
import BlurText from "./BlurText";
import { LiquidMetalButton } from "./ui/liquid-metal-button";
import Hls from "hls.js";

const HERO_VIDEO = "https://stream.mux.com/4IMYGcL01xjs7ek5ANO17JC4VQVUTsojZlnw4fXzwSxc.m3u8";

const technologies = [
  { name: "Vercel", icon: "https://cdn.simpleicons.org/vercel/white" },
  { name: "React", icon: "https://cdn.simpleicons.org/react" },
  { name: "Next.js", icon: "https://cdn.simpleicons.org/nextdotjs/white" },
  { name: "TypeScript", icon: "https://cdn.simpleicons.org/typescript" },
  { name: "Tailwind CSS", icon: "https://cdn.simpleicons.org/tailwindcss" },
  { name: "Stripe", icon: "https://cdn.simpleicons.org/stripe" },
  { name: "Figma", icon: "https://cdn.simpleicons.org/figma" },
  { name: "ElevenLabs", icon: "https://cdn.simpleicons.org/elevenlabs/white" },
  { name: "Claude", icon: "https://cdn.simpleicons.org/anthropic/white" },
  { name: "ChatGPT", icon: "https://cdn.simpleicons.org/openai/white" },
  { name: "Bun", icon: "https://cdn.simpleicons.org/bun/white" },
  { name: "Supabase", icon: "https://cdn.simpleicons.org/supabase" },
  { name: "Resend", icon: "https://cdn.simpleicons.org/resend/white" },
  { name: "Notion", icon: "https://cdn.simpleicons.org/notion/white" },
  { name: "Node.js", icon: "https://cdn.simpleicons.org/nodedotjs" },
  { name: "Python", icon: "https://cdn.simpleicons.org/python" },
  { name: "Docker", icon: "https://cdn.simpleicons.org/docker" },
  { name: "AWS", icon: "https://cdn.simpleicons.org/amazonaws/white" },
  { name: "PostgreSQL", icon: "https://cdn.simpleicons.org/postgresql" },
  { name: "MongoDB", icon: "https://cdn.simpleicons.org/mongodb" },
  { name: "Framer", icon: "https://cdn.simpleicons.org/framer/white" },
  { name: "GitHub", icon: "https://cdn.simpleicons.org/github/white" },
];

export default function Hero() {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(HERO_VIDEO);
      hls.attachMedia(video);
      return () => hls.destroy();
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = HERO_VIDEO;
    }
  }, []);

  return (
    <section
      id="hero"
      className="relative overflow-hidden"
      style={{ height: "1000px" }}
      data-testid="hero-section"
    >
      {/* Background Video */}
      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover z-0 pointer-events-none"
        data-testid="hero-video"
      />

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/40 z-0 pointer-events-none" />

      {/* Bottom gradient */}
      <div
        className="absolute bottom-0 left-0 right-0 z-[1] pointer-events-none"
        style={{
          height: "300px",
          background: "linear-gradient(to bottom, transparent, black)",
        }}
      />

      {/* Content */}
      <div
        className="relative z-10 flex flex-col items-center text-center h-full px-4 sm:px-8"
        style={{ paddingTop: "250px" }}
      >
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="liquid-glass rounded-full px-1 py-1 flex items-center gap-2 mb-8"
          data-testid="hero-badge"
        >
          <span className="bg-white text-black rounded-full px-3 py-1 text-xs font-semibold font-body">
            EsyAI
          </span>
          <span className="text-white text-xs font-medium font-body pr-3">
            AI-powered digital solutions for global businesses.
          </span>
        </motion.div>

        {/* Heading */}
        <BlurText
          text="Websites, Automation & AI That Drive Real Growth"
          className="text-5xl md:text-7xl lg:text-[5.5rem] font-heading italic text-white leading-[0.85] max-w-3xl tracking-[-3px] justify-center"
          delay={100}
          direction="bottom"
        />

        {/* Subtext */}
        <motion.p
          initial={{ filter: "blur(10px)", opacity: 0, y: 20 }}
          animate={{ filter: "blur(0px)", opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-6 text-sm md:text-base text-white/70 font-body font-light leading-relaxed max-w-lg"
          data-testid="hero-subtext"
        >
          We design high-performance websites, build AI-powered automations, and create custom software for ambitious businesses across the UK, India, the US, and Europe. Built to scale. Delivered fast.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ filter: "blur(10px)", opacity: 0, y: 20 }}
          animate={{ filter: "blur(0px)", opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.1 }}
          className="flex items-center gap-4 mt-8"
          data-testid="hero-cta"
        >
          <LiquidMetalButton
            label="Get Started"
            onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
          />
          <a
            href="#contact"
            className="text-white text-sm font-medium font-body flex items-center gap-2 hover:text-white/80 transition-colors"
            data-testid="hero-book-btn"
          >
            <Calendar className="w-4 h-4 fill-white" /> Book a Call
          </a>
        </motion.div>

        {/* Technologies Marquee */}
        <div className="mt-auto pb-8 pt-16 flex flex-col items-center gap-8 w-full max-w-[100vw] overflow-hidden relative">
          <div className="liquid-glass rounded-full px-4 py-1.5 z-10 relative">
            <span className="text-white/60 text-xs font-body font-light tracking-wide">
              OUR EXPERTISE IN
            </span>
          </div>
          
          <div 
            className="w-full flex overflow-hidden z-10 relative"
            style={{ 
              maskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)',
              WebkitMaskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)'
            }}
          >
            <motion.div
              className="flex w-max"
              animate={{ translateX: "-50%" }}
              transition={{
                duration: 40,
                repeat: Infinity,
                ease: "linear",
              }}
            >
              {[1, 2].map((set) => (
                <div key={set} className="flex gap-12 md:gap-20 pr-12 md:pr-20 w-max items-center">
                  {technologies.map((p, i) => (
                    <div
                      key={`tech-${set}-${i}`}
                      className="flex items-center justify-center shrink-0 opacity-50 hover:opacity-100 transition-opacity duration-300"
                    >
                      <img
                        src={p.icon}
                        alt={p.name}
                        className="h-8 md:h-9 w-auto object-contain"
                        loading="lazy"
                        title={p.name}
                      />
                    </div>
                  ))}
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
